/**
 * Metadata configuration for all pages in the application.
 * Each page has comprehensive SEO metadata including Open Graph and Twitter Card tags.
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7, 2.8, 2.9, 2.10, 7.3
 */

import envConfig from "./envConfig";

const getOgImage = () => {
  const base = envConfig.appUrl.endsWith("/") ? envConfig.appUrl.slice(0, -1) : envConfig.appUrl;
  if (base.includes("practido.com")) {
    return "https://www.practido.com/img/ai-lab-header.png";
  }
  return `${base}/img/ai-lab-header.png`;
};

export const pageMetadata = {
  studentSeo: {
    title: "Practido – India’s Leading E-Learning Online Platform",
    description: "Practido is an e-learning online platform offering smart study tools, practice tests, and optional online exam tests to help students learn faster and score higher.",
    keywords:"e-learning online platform",
    canonical:"https://practido.com/",
    ogTitle: "Practido – India’s Leading E-Learning Online Platform",
    ogDescription: "Practido is an e-learning online platform offering smart study tools, practice tests, and optional online exam tests to help students learn faster and score higher.",
    ogImage: "https://www.practido.com/img/OG-Image.png",
    ogUrl: "https://practido.com/",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterTitle: "Practido – India’s Leading E-Learning Online Platform",
    twitterDescription:
      "Practido is an e-learning online platform offering smart study tools, practice tests, and optional online exam tests to help students learn faster and score higher.",
  },
  schoolSeo: {
    title: "Test Papers for Schools | Practido IMO Olympiad Practice",
    description: "Prepare smarter with Practido’s test papers for schools. Access chapter-wise tests, mock exams, and IMO Olympiad practice to boost student performance.",
    keywords: 'test papers',
    canonical:"https://practido.com/practido-for-schools/",
    ogTitle: "Test Papers for Schools | Practido IMO Olympiad Practice",
    ogDescription: "Prepare smarter with Practido’s test papers for schools. Access chapter-wise tests, mock exams, and IMO Olympiad practice to boost student performance.",
    ogImage: "https://www.practido.com/img/OG-Image.png",
    ogUrl: "https://practido.com/practido-for-schools/",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterTitle: "Test Papers for Schools | Practido IMO Olympiad Practice",
    twitterDescription: "Prepare smarter with Practido’s test papers for schools. Access chapter-wise tests, mock exams, and IMO Olympiad practice to boost student performance.",
  },
  parentSeo: {
    title: "Practido Mock Test Platform for Kids | Free Mathematics App",
    description: "Help your child excel with Practido’s mock test practice and free mathematics app. Boost accuracy, confidence, and speed with expert-designed quizzes and tests.",
    keywords: 'mock test',
    canonical:"https://practido.com/practido-for-parents/",
    ogTitle: "Practido Mock Test Platform for Kids | Free Mathematics App",
    ogDescription: "Help your child excel with Practido’s mock test practice and free mathematics app. Boost accuracy, confidence, and speed with expert-designed quizzes and tests.",
    ogImage: "https://www.practido.com/img/OG-Image.png",
    ogUrl: "https://practido.com/practido-for-parents/",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterTitle: "Practido Mock Test Platform for Kids | Free Mathematics App",
    twitterDescription: "Help your child excel with Practido’s mock test practice and free mathematics app. Boost accuracy, confidence, and speed with expert-designed quizzes and tests.",
  },
  educatorsSeo: {
    title: "Practido for Educators – Question Bank & Math Olympiad Questions",
    description: "Access a complete question bank filled with ready math Olympiad questions for your classroom. Practido supports educators with reliable teaching content.",
    keywords: 'Question Bank',
    canonical:"https://practido.com/practido-for-educators/",
    ogTitle: "Practido for Educators – Question Bank & Math Olympiad Questions",
    ogDescription: "Access a complete question bank filled with ready math Olympiad questions for your classroom. Practido supports educators with reliable teaching content.",
    ogImage: "https://www.practido.com/img/OG-Image.png",
    ogUrl: "https://practido.com/practido-for-educators/",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterTitle: "Practido for Educators – Question Bank & Math Olympiad Questions",
    twitterDescription: "Access a complete question bank filled with ready math Olympiad questions for your classroom. Practido supports educators with reliable teaching content.",
  },
  aboutSeo: {
    title: "Your Practice Exam Partner | Educational Apps for Students",
    description: "Learn how Practido helps students prepare smarter with olympiad practice exam. Discover why it’s counted among the best educational apps for effective learning.",
    keywords: 'practice exam',
    canonical:"https://practido.com/about/",
    ogTitle: "Your Practice Exam Partner | Educational Apps for Students",
    ogDescription: "Learn how Practido helps students prepare smarter with olympiad practice exam. Discover why it’s counted among the best educational apps for effective learning.",
    ogImage: "https://www.practido.com/img/OG-Image.png",
    ogUrl: "https://practido.com/about/",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterTitle: "Your Practice Exam Partner | Educational Apps for Students",
    twitterDescription: "Learn how Practido helps students prepare smarter with olympiad practice exam. Discover why it’s counted among the best educational apps for effective learning.",
  },
  schoolRegistrationSeo: {
    title: "School Partnership Program – Register Your School on Practido",
    description: "Practido offers an easy school partnership program with access to online tests, practice papers, and complete performance tracking for students.",
    keywords: 'school partnership',
    canonical:"https://practido.com/register/school/",
    ogTitle: "School Partnership Program – Register Your School on Practido",
    ogDescription: "Practido offers an easy school partnership program with access to online tests, practice papers, and complete performance tracking for students.",
    ogImage: "https://www.practido.com/img/OG-Image.png",
    ogUrl: "https://practido.com/register/school/",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterTitle: "School Partnership Program – Register Your School on Practido",
    twitterDescription: "Practido offers an easy school partnership program with access to online tests, practice papers, and complete performance tracking for students.",
  },
  contactSeo: {
    title: "Contact Practido for Free Online Practice Test Support",
    description: "Get in touch with Practido for help with free online practice tests, exams, and platform support. Our team is here to assist students, parents, and educators.",
    keywords: 'free online practice test',
    canonical:"https://practido.com/contactus/",
    ogTitle: "Contact Practido for Free Online Practice Test Support",
    ogDescription: "Get in touch with Practido for help with free online practice tests, exams, and platform support. Our team is here to assist students, parents, and educators.",
    ogImage: "https://www.practido.com/img/OG-Image.png",
    ogUrl: "https://practido.com/contactus/",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterTitle: "Contact Practido for Free Online Practice Test Support",
    twitterDescription: "Get in touch with Practido for help with free online practice tests, exams, and platform support. Our team is here to assist students, parents, and educators.",
  },
  ourAppSeo: {
    title: "Olympiad Preparation Apps by Practido | Practice Anytime",
    description: "Practido offers easy Olympiad preparation with smart apps, mock tests, and practice papers. Help students improve speed, accuracy, and exam confidence.",
    keywords:"Olympiad and talent exam preparation, platform for various Olympiads, English Olympiad preparation, preparing for multiple types of Olympiads, such as Science, Math, and English.",
    canonical:"https://practido.com/olympiad-preparation-apps/",
    ogTitle: " Olympiad Preparation Apps by Practido | Practice Anytime",
    ogDescription: "Practido offers easy Olympiad preparation with smart apps, mock tests, and practice papers. Help students improve speed, accuracy, and exam confidence.",
    ogImage: "https://www.practido.com/img/OG-Image.png",
    ogUrl: "https://practido.com/olympiad-preparation-apps/",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterTitle: " Olympiad Preparation Apps by Practido | Practice Anytime",
    twitterDescription: "Practido offers easy Olympiad preparation with smart apps, mock tests, and practice papers. Help students improve speed, accuracy, and exam confidence.",
  },
  PrivacySeo: {
    title: "Privacy Policy - Practido",
    canonical:"https://practido.com/privacy/",
    ogTitle: "Privacy Policy - Practido",
    ogImage: "https://www.practido.com/img/OG-Image.png",
    ogUrl: "https://practido.com/privacy/",
    ogType: "website",
    twitterCard: "summary_large_image",
  },
  termSeo: {
    title: "Terms of Service - Practido",
    canonical:"https://practido.com/terms/",
    ogTitle: "Terms of Service - Practido",
    ogImage: "https://www.practido.com/img/OG-Image.png",
    ogUrl: "https://practido.com/terms/",
    ogType: "website",
    twitterCard: "summary_large_image",
  },
  aiLabSeo: {
    title: "AI & Robotics Classes for Kids | Coding, STEM & Future Skills | Practido",
    description: "Join Practido Learning Center for AI, Robotics, Coding, STEM, Design and Future Skills. Hands-on projects, expert mentors and certifications for students aged 6–18.",
    keywords: "Live robotics classes for kids, coding courses, AI classes, 3D modelling, design classes, smart money skills, AI and robotics classes for kids, coding classes for kids, robotics classes for kids, STEM learning center, future skills classes for kids",
    canonical: "https://practido.com/ai-robotics-coding-classes-for-kids",
    ogTitle: "Practido Learning Center | AI, Robotics & Coding Classes for Kids",
    ogDescription: "Live classes in AI, Robotics, Coding, STEM and Design for kids aged 6–18. Real projects. Expert mentors. Book a free demo today.",
    ogImage: getOgImage(),
    ogUrl: "https://practido.com/ai-robotics-coding-classes-for-kids",
    ogType: "website",
    ogImageAlt: "Kids learning AI and robotics at Practido Learning Center",
    robots: "index, follow",
    twitterCard: "summary_large_image",
    twitterTitle: "Practido Learning Center | AI, Robotics & Coding Classes for Kids",
    twitterDescription: "Live classes in AI, Robotics, Coding, STEM and Design for kids aged 6–18. Real projects. Expert mentors. Book a free demo today.",
  },
  registerSeo: {
    title: "Create an Account | Practido",
    description: "Sign up for Practido to start preparing for Olympiads, mathematics competitions, and future skills programs.",
    keywords: "register, sign up, olympiad practice, online learning, practido account",
    canonical: "https://practido.com/register/",
    ogTitle: "Create an Account | Practido",
    ogDescription: "Sign up for Practido to start preparing for Olympiads, mathematics competitions, and future skills programs.",
    ogImage: "https://www.practido.com/img/OG-Image.png",
    ogUrl: "https://practido.com/register/",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterTitle: "Create an Account | Practido",
    twitterDescription: "Sign up for Practido to start preparing for Olympiads, mathematics competitions, and future skills programs.",
  },
  loginSeo: {
    title: "Sign In to Practido",
    description: "Sign in to your Practido account to access online practice tests, mock exams, and your personalized student dashboard.",
    keywords: "sign in, login, practido login, olympiad exam login",
    canonical: "https://practido.com/login/",
    ogTitle: "Sign In to Practido",
    ogDescription: "Sign in to your Practido account to access online practice tests, mock exams, and your personalized student dashboard.",
    ogImage: "https://www.practido.com/img/OG-Image.png",
    ogUrl: "https://practido.com/login/",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterTitle: "Sign In to Practido",
    twitterDescription: "Sign in to your Practido account to access online practice tests, mock exams, and your personalized student dashboard.",
  },
  forgotPasswordSeo: {
    title: "Forgot Password | Practido",
    description: "Reset your Practido account password to regain access to your practice tests and learning dashboard.",
    keywords: "forgot password, reset password, practido password recovery",
    canonical: "https://practido.com/login/forgotPassword/",
    ogTitle: "Forgot Password | Practido",
    ogDescription: "Reset your Practido account password to regain access to your practice tests and learning dashboard.",
    ogImage: "https://www.practido.com/img/OG-Image.png",
    ogUrl: "https://practido.com/login/forgotPassword/",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterTitle: "Forgot Password | Practido",
    twitterDescription: "Reset your Practido account password to regain access to your practice tests and learning dashboard.",
  },
  schoolLoginSeo: {
    title: "School Sign In | Practido",
    description: "Sign in to your Practido School Partner portal to manage registered students and olympiad assessments.",
    keywords: "school login, school portal, practido school",
    canonical: "https://practido.com/login/school/",
    ogTitle: "School Sign In | Practido",
    ogDescription: "Sign in to your Practido School Partner portal to manage registered students and olympiad assessments.",
    ogImage: "https://www.practido.com/img/OG-Image.png",
    ogUrl: "https://practido.com/login/school/",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterTitle: "School Sign In | Practido",
    twitterDescription: "Sign in to your Practido School Partner portal to manage registered students and olympiad assessments.",
  },
};

export default pageMetadata;
